import React, { useState, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import pica from 'pica';

const options = {
  poster: { width: 300, height: 450 },
  thumb: { width: 425, height: 223 },
  lista: { width: 768, height: 272 },
  header: { width: 1440, height: 900 },
  thumbNoverama: { width: 368, height: 204 },
  coverMobile: { width: 462, height: 260 },
  coverDesktop: { width: 1262, height: 710 },
  coverTablet: { width: 1387, height: 780 },
};

const qualityOptions = {
  veryLow: 0.2,
  low: 0.4,
  medium: 0.6,
  high: 0.8,
  veryHigh: 1,
};

const ArtExtractorManual = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedOption, setSelectedOption] = useState('poster');
  const [selectedQualityOption, setSelectedQualityOption] = useState('high');
  const [showCropper, setShowCropper] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  
  const fileInputRef = useRef(null);
  const toast = useRef(null);

  const { width, height } = options[selectedOption];

  const createImage = url =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = err => reject(err);
    });

  const dataURLtoBlob = dataurl => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const getCroppedImg = async (imgSrc, pixelCrop) => {
    const image = await createImage(imgSrc);
    
    // First canvas for cropping
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = pixelCrop.width;
    cropCanvas.height = pixelCrop.height;
    const cropCtx = cropCanvas.getContext('2d');
    
    cropCtx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
    
    // Second canvas for resizing to exact dimensions
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = width; // Using the selected option width
    finalCanvas.height = height; // Using the selected option height
    
    // Using pica for high quality resizing
    await pica().resize(cropCanvas, finalCanvas);
    
    return new Promise((resolve, reject) => {
      if (finalCanvas.toBlob) {
        finalCanvas.toBlob(
          blob => {
            if (!blob) reject(new Error('Canvas is empty'));
            else resolve(blob);
          },
          'image/webp',
          qualityOptions[selectedQualityOption]
        );
      } else {
        const dataUrl = finalCanvas.toDataURL('image/webp', qualityOptions[selectedQualityOption]);
        resolve(dataURLtoBlob(dataUrl));
      }
    });
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setFileName(file.name.split('.')[0]); // Save the file name without extension

    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(reader.result);
      setShowCropper(true);
      setIsLoading(false);
    };
    reader.onerror = () => {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load image',
        life: 3000
      });
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDownload = async () => {
    if (!croppedAreaPixels) {
      toast.current.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'You need to crop the image first',
        life: 3000
      });
      return;
    }
    
    try {
      const blob = await getCroppedImg(imageUrl, croppedAreaPixels);
      const urlBlob = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      
      // Use original filename if available, otherwise use default
      const downloadName = fileName 
        ? `${fileName}_${selectedOption}.webp` 
        : `cropped-image_${selectedOption}.webp`;
        
      link.download = downloadName;
      link.click();
      
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Image exported successfully',
        life: 3000
      });
    } catch (err) {
      console.error(err);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export image',
        life: 3000
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const resetCropper = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="art-extractor-container">
      <Toast ref={toast} />
      
      <div className="upload-section">
        <h2>Art Extracto v2</h2>
        <p>Upload an image and crop it to the desired dimensions</p>
        
        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          <Button
            label={isLoading ? 'Loading...' : 'Select Image'}
            icon="pi pi-upload"
            className="p-button-primary mr-2"
            onClick={triggerFileInput}
            disabled={isLoading}
          />
          
          <select 
            value={selectedOption} 
            onChange={e => setSelectedOption(e.target.value)}
            className="format-select"
          >
            <option value="poster">Poster: 300x450 (webp up to 30kb)</option>
            <option value="thumb">Thumb: 425x223 (webp up to 150kb)</option>
            <option value="lista">List: 768x272 (webp up to 150kb)</option>
            <option value="header">Header: 1440x900 (webp up to 150kb)</option>
            <option value="thumbNoverama">Thumb (Noverama): 368x204 (webp up to 150kb)</option>
            <option value="coverMobile">Cover Mobile: 462x260 (webp up to 150kb)</option>
            <option value="coverDesktop">Cover Desktop: 1262x710 (webp up to 150kb)</option>
            <option value="coverTablet">Cover Tablet: 1387x780 (webp up to 150kb)</option>
          </select>
          <select
            value={selectedQualityOption}
            onChange={e => setSelectedQualityOption(e.target.value)}
            className="format-select"
          >
            <option value="veryLow">Very Low (20%)</option>
            <option value="low">Low (40%)</option>
            <option value="medium">Medium (60%)</option>
            <option value="high">High (80%)</option>
            <option value="veryHigh">Very High (100%)</option>
          </select>
        </div>
        
        {imageUrl && (
          <div className="image-info">
            <span>Loaded image: {fileName || 'image'}</span>
            <Button
              label="Reset Crop"
              icon="pi pi-refresh"
              className="p-button-text"
              onClick={resetCropper}
            />
          </div>
        )}
      </div>

      {imageUrl && showCropper && (
        <div className="cropper-container">
          <div style={{ position: 'relative', width, height, margin: '20px 0' }}>
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              aspect={width / height}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(area, pixels) => setCroppedAreaPixels(pixels)}
            />
          </div>
          
          <div className="controls">
            <label>Zoom:</label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
            />
            
            <Button
              label="Export"
              icon="pi pi-download"
              className="p-button-success"
              onClick={handleDownload}
            />
          </div>
        </div>
      )}
      
      <style jsx>{`
        .art-extractor-container {
          max-width: 1200px;
          margin: 0;
          padding: 20px;
          font-family: Arial, sans-serif;
          text-align: left;
        }
        
        .upload-section {
          margin-bottom: 20px;
        }
        
        .upload-controls {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          align-items: center;
        }
        
        .format-select {
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #ccc;
        }
        
        .image-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
        }
        
        .cropper-container {
          background-color: #f5f5f5;
          padding: 20px;
          border-radius: 8px;
          text-align: left;
        }
        
        .controls {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-top: 15px;
        }
        
        .controls input[type="range"] {
          flex: 1;
          max-width: 200px;
        }
      `}</style>
    </div>
  );
};

export default ArtExtractorManual;